import {DocSection} from './_models'

const DOC_FILES = ['SCOPE.md', 'PRD.md', 'ARCHITECTURE.md', 'AI_CONTEXT.md']

const STOPWORDS = new Set([
  'yang',
  'dan',
  'untuk',
  'dengan',
  'dari',
  'ke',
  'di',
  'pada',
  'adalah',
  'sih',
  'kah',
  'saja',
  'itu',
  'tidak',
  'bukan',
  'juga',
  'atau',
  'agar',
  'bisa',
  'harus',
  'akan',
  'telah',
  'sudah',
  'para',
  'dalam',
  'sebuah',
  'tersebut',
  'per',
  'nya',
  'bagaimana',
  'mengapa',
  'kenapa',
  'mana',
  'dimana',
  'kapan',
])

// Ekspansi sinonim agar keyword matching menangkap variasi pertanyaan,
// termasuk 5 pertanyaan wajib ("aplikasi ini apa", "proses bisnisnya bagaimana",
// "fitur apa yang ditambahkan", "apa yang diimplementasikan", "pengembangan ke depan").
const SYNONYM_MAP: Record<string, string[]> = {
  // "aplikasi ini apa"
  ini: ['aplikasi', 'platform', 'dashboard', 'alaya', 'ringkasan', 'repo'],
  apa: ['aplikasi', 'platform', 'dashboard', 'alaya', 'ringkasan', 'repo'],
  aplikasi: ['aplikasi', 'platform', 'dashboard', 'alaya', 'ringkasan', 'repo'],
  app: ['aplikasi', 'platform', 'dashboard', 'alaya', 'ringkasan', 'repo'],
  platform: ['aplikasi', 'platform', 'dashboard', 'alaya', 'ringkasan'],
  dashboard: ['aplikasi', 'dashboard', 'platform', 'alaya'],
  // "proses bisnisnya bagaimana"
  bisnis: ['alur', 'bisnis', 'proses', 'tahap', 'siklus', 'workflow'],
  proses: ['alur', 'bisnis', 'proses', 'tahap', 'siklus', 'workflow'],
  alur: ['alur', 'bisnis', 'proses', 'tahap', 'siklus', 'workflow', 'step'],
  tahap: ['alur', 'bisnis', 'proses', 'tahap', 'siklus', 'step'],
  siklus: ['alur', 'bisnis', 'proses', 'tahap', 'siklus', 'step'],
  cara: ['alur', 'proses', 'tahap', 'step', 'workflow'],
  // "fitur apa yang ditambahkan"
  fitur: [
    'fitur',
    'generate',
    'enhance',
    'chatbot',
    'ai',
    'gambar',
    'deskripsi',
    'kemampuan',
    'luwie',
    'thumbnail',
  ],
  tambah: [
    'fitur',
    'generate',
    'enhance',
    'chatbot',
    'ai',
    'gambar',
    'deskripsi',
    'ditambahkan',
    'baru',
  ],
  ditambahkan: ['fitur', 'generate', 'enhance', 'chatbot', 'ai', 'gambar', 'deskripsi', 'baru'],
  baru: ['fitur', 'chatbot', 'generate', 'enhance', 'ai', 'baru'],
  // "apa yang diimplementasikan"
  implementasi: [
    'implementasi',
    'diimplementasikan',
    'terimplementasi',
    'endpoint',
    'kode',
    'dibuat',
    'alur',
    'generate',
    'enhance',
    'chatbot',
  ],
  diimplementasikan: [
    'implementasi',
    'diimplementasikan',
    'terimplementasi',
    'endpoint',
    'kode',
    'alur',
    'generate',
    'enhance',
    'chatbot',
  ],
  terimplementasi: [
    'implementasi',
    'diimplementasikan',
    'terimplementasi',
    'endpoint',
    'alur',
    'generate',
    'enhance',
    'chatbot',
  ],
  dibuat: ['implementasi', 'diimplementasikan', 'dibuat', 'endpoint', 'script', 'kode', 'chatbot'],
  endpoint: ['implementasi', 'endpoint', 'chat', 'generate', 'enhance', 'api'],
  script: ['implementasi', 'script', 'copy', 'build', 'dibuat'],
  // "pengembangan ke depan bagaimana"
  pengembangan: [
    'pengembangan',
    'depan',
    'rencana',
    'belum',
    'chatbot',
    'berikutnya',
    'roadmap',
    'diisi',
    'belum diisi',
  ],
  depan: [
    'pengembangan',
    'depan',
    'rencana',
    'belum',
    'chatbot',
    'berikutnya',
    'roadmap',
    'diisi',
    'belum diisi',
  ],
  rencana: ['pengembangan', 'rencana', 'depan', 'belum', 'chatbot', 'roadmap', 'belum diisi'],
  berikutnya: ['pengembangan', 'berikutnya', 'rencana', 'depan', 'belum', 'roadmap'],
  roadmap: ['pengembangan', 'roadmap', 'rencana', 'depan', 'belum'],
  // istilah AI
  chatbot: ['chatbot', 'luwie', 'ai', 'chat', 'asisten', 'generate', 'enhance'],
  luwie: ['chatbot', 'luwie', 'ai', 'chat', 'asisten'],
  ai: ['ai', 'chatbot', 'generate', 'enhance', 'gambar', 'deskripsi', 'luwie'],
  generate: ['generate', 'gambar', 'thumbnail', 'ai', 'image', 'fitur'],
  enhance: ['enhance', 'deskripsi', 'ai', 'fitur'],
  gambar: ['generate', 'gambar', 'thumbnail', 'image', 'ai'],
  deskripsi: ['enhance', 'deskripsi', 'ai', 'fitur'],
}

// ——— Small talk / sapaan ———
// Dijawab lokal (tanpa panggil backend) agar sapaan sederhana seperti "hi"
// tidak berakhir dengan "tidak ada di dokumentasi" dan tidak boros token AI.
// Urutan pola penting: pola lebih spesifik (mis. "selamat pagi") di atas
// pola generik ("halo") agar tidak salah tangkap.
const SMALLTALK: {pattern: RegExp; reply: string}[] = [
  {
    pattern:
      /^(selamat\s+(pagi|siang|sore|malam|datang)|good\s+(morning|afternoon|evening|day)|assalamu'?alaikum|assalamualaikum|salam\s*(\w+\s*)*|wr\s*wb)\b/i,
    reply:
      'Halo! Ada yang bisa saya bantu seputar aplikasi Alaya? Saya bisa jelaskan fitur yang tersedia, proses bisnis, implementasi teknis, sampai rencana pengembangan ke depan.',
  },
  {
    pattern: /^(hi+|hai+|hel+o+|halo+|hallo+|hay+|hey+|yo+|hiii?)\b/i,
    reply:
      'Halo! Ada yang bisa saya bantu seputar aplikasi Alaya? Saya bisa jelaskan fitur yang tersedia, proses bisnis, implementasi teknis, sampai rencana pengembangan ke depan.',
  },
  {
    pattern:
      /\b(siapa\s+(kamu|luwie)|kamu\s+siapa|luwie\s+itu\s+(apa|siapa)|apa\s+itu\s+luwie|kamu\s+(bisa|bisanya)\s+apa|bisa\s+apa\s+aja)\b/i,
    reply:
      'Saya LUWIE, asisten virtual tim Alaya. Saya membantu menjawab pertanyaan seputar aplikasi: fitur yang tersedia, proses bisnis, implementasi teknis, sampai roadmap pengembangan. Tanya saja!',
  },
  {
    pattern: /^(terima\s*kasih|makasih|mksh|thanks|thank\s*you|thx|tq|matur\s*nuwun)\b/i,
    reply: 'Sama-sama! Senang bisa membantu. Ada lagi yang ingin ditanyakan?',
  },
  {
    pattern:
      /^(bye|dadah|selamat\s+tinggal|sampai\s+jumpa|goodbye|good\s*bye|see\s+(you|ya)|sampai\s+ketemu)\b/i,
    reply: 'Sampai jumpa! Kalau butuh bantuan lagi, LUWIE siap membantu.',
  },
]

/**
 * Deteksi sapaan/small talk. Mengembalikan jawaban siap pakai jika pertanyaan
 * cocok dengan pola sapaan, atau null jika harus diteruskan ke backend AI.
 */
export function detectSmallTalk(question: string): string | null {
  const q = question.trim()
  if (!q) return null
  for (const {pattern, reply} of SMALLTALK) {
    if (pattern.test(q)) return reply
  }
  return null
}

const TOP_SECTIONS = 3
const MAX_CONTEXT_CHARS = 6000

let cache: DocSection[] | null = null

export async function getSections(): Promise<DocSection[]> {
  if (cache) return cache

  const sections: DocSection[] = []
  for (const file of DOC_FILES) {
    try {
      const res = await fetch(`/docs/${file}`)
      if (!res.ok) continue
      const md = await res.text()
      sections.push(...parseMarkdown(file, md))
    } catch (err) {
      console.error(`[luwie] gagal memuat ${file}`, err)
    }
  }
  cache = sections
  return sections
}

export function parseMarkdown(file: string, md: string): DocSection[] {
  const lines = md.split('\n')
  const sections: DocSection[] = []
  let heading = ''
  let content: string[] = []

  const flush = () => {
    const text = content.join('\n').trim()
    if (text) {
      sections.push({file, heading, content: text})
    }
    content = []
  }

  for (const line of lines) {
    const m = line.match(/^(#{1,6})\s+(.*)$/)
    if (m) {
      flush()
      heading = m[2].trim()
    } else {
      content.push(line)
    }
  }
  flush()

  return sections
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 1 && !STOPWORDS.has(t))
}

function searchTermsFor(token: string): string[] {
  const terms = new Set<string>([token])
  const extra = SYNONYM_MAP[token]
  if (extra) {
    extra.forEach((e) => terms.add(e))
  }
  return Array.from(terms)
}

export function searchSections(
  query: string,
  sections: DocSection[],
  topN = TOP_SECTIONS
): DocSection[] {
  const tokens = tokenize(query)
  const terms = new Set<string>()
  tokens.forEach((t) => searchTermsFor(t).forEach((x) => terms.add(x)))

  const scored = sections.map((s) => {
    const text = `${s.heading}\n${s.content}`.toLowerCase()
    const heading = s.heading.toLowerCase()
    let score = 0
    terms.forEach((term) => {
      if (term.length < 2) return
      if (heading.includes(term)) score += 2
      else if (text.includes(term)) score += 1
    })
    return {s, score}
  })

  scored.sort((a, b) => b.score - a.score)

  const top = scored.filter((x) => x.score > 0).slice(0, topN)
  if (top.length > 0) {
    return top.map((x) => x.s)
  }

  // Ambang longgar: jika tidak ada kecocokan kata, tetap kirim section inti
  // agar pertanyaan umum tetap bisa dijawab (model yang memutuskan "tidak tahu").
  return fallbackSections(sections, topN)
}

function fallbackSections(sections: DocSection[], topN: number): DocSection[] {
  const core: DocSection[] = []
  const keys = [
    'ringkasan aplikasi',
    'alur bisnis inti',
    'deskripsi',
    'tujuan',
    'peta seluruh sistem',
  ]
  for (const key of keys) {
    const found = sections.find((s) => s.heading.toLowerCase().includes(key))
    if (found) core.push(found)
  }
  for (const s of sections) {
    if (core.length >= topN) break
    if (!core.includes(s)) core.push(s)
  }
  return core.slice(0, topN)
}

export function buildContext(sections: DocSection[], maxChars = MAX_CONTEXT_CHARS): string {
  const blocks: string[] = []
  let total = 0
  for (const s of sections) {
    const block = `[${s.file} — ${s.heading}]\n${s.content}`
    if (total + block.length > maxChars) {
      const remaining = maxChars - total
      if (remaining > 200) blocks.push(block.slice(0, remaining))
      break
    }
    blocks.push(block)
    total += block.length
  }
  return blocks.join('\n\n')
}
